import React, { useMemo, forwardRef } from 'react'
import { Box, Stack, Typography, Tooltip, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Status from 'components/global/Status'

import useMessages from '../use-messages'
import { getDeviceStatus } from '../device-status'

const DeviceInfos = ({ device }) => {
  const { t } = useTranslation()
  return (
    <Stack>
      <Box>{device.model}</Box>
      <Box>
        {t('stream-cast.card.devices.mac_address')} : {device.mac || '-'}
      </Box>
      <Box>
        {t('stream-cast.card.devices.serial_number')} : {device.serial || '-'}
      </Box>
    </Stack>
  )
}

const StatusIcon = forwardRef(({ device, ...props }, ref) => {
  const statusType = getDeviceStatus(device)

  const statusColors = {
    operational: '#92BC62',
    unreachable: '#EEA20A',
    not_configured: '#EEA20A'
  };

  return (
    <Box
      ref={ref}
      {...props}
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: statusColors[statusType]
      }}
    >
      <i
        className={`fa-${device.kind === 'tv' ? 'regular' : 'brands'} fa-${device.kind === 'tv' ? 'tv' : 'chromecast'}`}
        style={{ fontSize: device.kind === 'tv' ? '13px' : '16px' }}
      />
    </Box>
  )
})

const Devices = ({ devices }) => {
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      {devices.map((device) => (
        <Tooltip
          key={device.uuid}
          title={<DeviceInfos device={device} />}
          placement="top"
          arrow
        >
          <StatusIcon device={device} />
        </Tooltip>
      ))}
    </Box>
  )
}

const MessageStatus = ({ room }) => {
  const { t } = useTranslation()
  const { messages } = useMessages()
  const hasActiveMessage = useMemo(() => {
    const message = messages.find(message => message.site_sf_id === room.site_sf_id)
    return message?.content?.trim()
  }, [messages, room.site_sf_id])
  
  
  return hasActiveMessage ? (
    <Status 
      status={t('stream-cast.message.in_progress')} 
      color="#547231"
      bgcolor="#E1EDD4"
      height="20px"
      fontSize={10}
      isUppercase={true}
    />
  ) : (
    <Status 
      status="-"
      color="#7A8096"
      bgcolor="#F6F7F8"
      height="20px"
      fontSize={10}
      isUppercase={true}
    />
  )
}

const RoomNameWithCount = ({ name, count }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2">{name}</Typography>
      {count && (
        <Chip 
          label={count} 
          size="small" 
          sx={{ 
            height: 20, 
            fontSize: 10, 
            fontWeight: 500,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'grey.100'
          }} 
        />
      )}
    </Box>
  )
}

const COLUMNS_CONFIG = [{
  name: 'room',
  header: {
    label: (ctx) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {ctx.t('stream-cast.table.room_col')}
      </Box>
    )
  },
  value: (room) => <RoomNameWithCount name={room.name} />,
  sortBy: 'room',
  sortable: true
}, {
  name: 'site_name',
  header: {
    label: (ctx) => ctx.t('stream-cast.table.site')
  },
  value: (room) => room.site_name,
  sortBy: 'site_name',
  sortable: true
}, {
  name: 'wifi-key',
  header: {
    label: (ctx) => ctx.t('stream-cast.table.wifi_key_col')
  },
  value: (room) => room.wifi_psk,
}, {
  name: 'devices',
  header: {
    label: (ctx) => ctx.t('stream-cast.table.devices_col')
  },
  value: (room) => <Devices devices={room.devices} />,
}, {
  name: 'message',
  header: {
    label: (ctx) => ctx.t('stream-cast.table.message_col')
  },
  value: (room) => <MessageStatus room={room} />,
}]

export default COLUMNS_CONFIG
